import React, { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Box from "@mui/material/Box";

import FrontHeader from "../../components/front-end/Header";
import HeroSlanting from "../../components/front-end/HeroSlanting"

const BenifitsSection = lazy(() => import("../../components/front-end/benifitsSection"));
const ClientStepsSection = lazy(() => import("../../components/front-end/clientStepsSection"));
const ServicesSection = lazy(() => import("../../components/front-end/servicesSection"));
const ExpertSection = lazy(() => import("../../components/front-end/expertSection"));
const Cta = lazy(() => import("../../components/front-end/cta"));
const CustomFaq = lazy(() => import("../../components/front-end/CustomFaq"))
const ScamProof = lazy(() => import("../../components/front-end/scamProof"));
const Footer = lazy(() => import("../../components/front-end/footer"));

const heroFaqData = [
  {
    topic: `What kind of academic writing solutions do Acadlance provide?`,
    description: `Some assignment types for which Acadlance provides solutions include essays, research
    papers, dissertations, thesis papers, lab reports, book/literature reviews, and case studies. So,
    no matter what type of assignment troubles you, Acadlance has a solution.`
  },
  {
    topic: `What is the experience of the freelance writers?`,
    description: `All our writers have completed a college course relevant to the subject or topic they work on.
    Some of them possess master’s and PhD’s from universities that are accredited worldwide.
    They are also trained professionals in academic literature research and writing.`
  },
  {
    topic: `How does the posting/bidding process for academic writing work at Acadlance?`,
    description: `Upload the details of the academic writing assignment you are willing to get done. Writers will
    see it and offer their bid with other details about themselves and their writing skills. Here, you
    put your orders to the platform and wait for the writers to bid on them. Then, you can select the
    one that meets your needs based on their rating and previous clients’ feedback.`
  },
  {
    topic: `Is the academic writing I get on Acadlance free from plagiarism and original?`,
    description: `Yes, all work is compiled for your order according to your specifications. The documents are
    checked for plagiarism before submission and reviewed for quality before being delivered.
    However, if you are dissatisfied with the quality of the work, we provide three free revisions.`
  },
  {
    topic: `Do I need past freelance work experience as an academic writer to qualify?`,
    description: `No prior freelancing is required! We have an open-door policy regarding applicants, and we only
    consider individuals who have good research and writing skills in their discipline. Start your
    freelance account and start building your portfolio on Acadlance.`
  },
  {
    topic: `In what areas of specialization can academic writers provide services to their clients?`,
    description: `Writers can offer academic work help in all primary and other academic fields of study, such as
    sciences, social sciences, humanities and technology, engineering, statistics, and more.`
  },
  {
    topic: `How soon can I expect to have my academic papers written for me on Acadlance?`,
    description: `Delivery of completed projects takes different durations depending on the amount of work
    involved, but emergency orders can be processed at an additional charge. Often, the orders can
    be accomplished within the 24-hour time frame for urgent tasks.`
  },
  {
    topic: `Does Acadlance have a list of freelance academic writers, and does the platform
    conduct checks on the freelancers?`,
    description: `We check their identity, education, and skill verifications to ensure they are safe working with 
    students and clients on the platform. We also review their samples and analyze the strategies’
    efficacy.`
  },
  {
    topic: `Is the money I give the platform secure until my work is done?`,
    description: `Client funds are kept securely in custody and only paid out if work is authorized in compliance
    with site guidelines. We aim to provide our customers with an all-inclusive security and
    satisfaction guarantee.`
  },
  {
    topic: `Can I connect with the writer if I am unsatisfied with the task?`,
    description: `You can contact the writer until you are delighted with the task. The writers will make the
    required changes.`
  }
]

const FrontEnd = () => {
  const MarginBox = (height) => <Box sx={{ height }} />;

  const margin = "1rem";
  const marginCS = "3rem";
  const msg1 = "Discover, Hire, and Thrive with Top Talent!";
  const msg2 = "Unleash Your Academic Excellence with our Platform!";
  const msg3 = "Navigate your journey to success effortlessly with us!";

  const style1 = {
    px: { xl: "3rem", lg: "3rem", md: "3rem", sm: "1rem", xs: "1rem" },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        width: "100%"
      }}
    >
      <Helmet>
        <title>Acadlance - Marketplace for Freelance Academic Writing</title>
        <meta
          name="description"
          content="Acadlance connects students with professional academic writers, creating opportunities for innovative and successful academic projects."
        />
        <meta
          name="keywords"
          content="Acadlance, Marketplace for Academic Writing, Academic Writing, Assignment Writing, expert, client, application, dashboard, acadlance, ACADLANCE, Acadlance, assignments, academic, writing, freelance"
        />
        <link rel="icon" href="/favicon-acadlance/favicon.ico" />
      </Helmet>

      <FrontHeader />
      <header>
        <Box sx={{ backgroundColor: '#FFFFFF' }}>
          <HeroSlanting />
        </Box>
      </header>
      <main>
        <Suspense fallback={<div style={{ marginTop: '150vh' }}></div>}>
          <Box sx={{ backgroundColor: '#FFFFFF', px: { xl: "3rem", lg: "3rem", md: "3rem", sm: "1rem", xs: "1rem" } }}>
            <MarginBox height={marginCS} />
            <BenifitsSection />
            <MarginBox height={marginCS} />
          </Box>
        </Suspense>
        <Suspense fallback={<div></div>}>
          <Box sx={style1}>
            <MarginBox height={marginCS} />
            <ClientStepsSection />
            <Box sx={{ marginTop: '2.5rem !important' }}>
              <Cta msg={msg1} />
            </Box>
            <MarginBox height={margin} />
            <MarginBox height={marginCS} />
          </Box>
        </Suspense>
        <Suspense fallback={<div></div>}>
          <Box sx={{
            backgroundColor: '#FFFFFF',
            px: { xl: "3rem", lg: "3rem", md: "3rem", sm: "1rem", xs: "1rem" },
            paddingTop: { xl: "3rem", lg: "3rem", md: "3rem", sm: "3rem", xs: "3rem" }
          }}>
            <ServicesSection />
            <MarginBox height={margin} />
          </Box>
        </Suspense>
        <Suspense fallback={<div></div>}>
          <Box sx={style1}>
            <MarginBox height={marginCS} />
            <ExpertSection />
            <Box sx={{ marginTop: '2rem !important' }}>
              <Cta msg={msg2} />
            </Box>
            <MarginBox height={margin} />
            <MarginBox height={marginCS} />
          </Box>
        </Suspense>
        <Suspense fallback={<div></div>}>
          <Box sx={{
            backgroundColor: '#FEFEFE',
            px: { xl: "3rem", lg: "3rem", md: "3rem", sm: "1rem", xs: "1rem" },
          }}>
            <MarginBox height={marginCS} />
            <ScamProof />
            <Cta msg={msg3} />
            <MarginBox height={margin} />
            <MarginBox height={marginCS} />
          </Box>
        </Suspense>
        <Suspense fallback={<div></div>}>
          <Box sx={style1}>
            <MarginBox height={marginCS} />
            <Box sx={{ backgroundColor: 'transparent' }}>
              <CustomFaq faqData={heroFaqData} showAbsoluteDesign={true} />
            </Box>
            <MarginBox height={marginCS} />
          </Box>
        </Suspense>
      </main>
      <Suspense fallback={<div></div>}>
        <Footer />
      </Suspense>
    </Box >
  );
};

export default FrontEnd;
